import type { JSX } from 'solid-js';
import { Show } from 'solid-js';

// Status pill: a bordered mono pill whose border and text take the tone's hue
// (`ui/_badge.scss`). `live` adds the one pulsing live dot. Text comes from the
// caller, keyed.
//
// Not `@developerz/ui`'s `Chip`: a chip has no tone by design (it never carries
// a status hue), and the tone IS this component's job.

export type StatusTone = 'success' | 'live' | 'warning' | 'danger' | 'neutral';

// Each tone keeps the `badge--*` variant it always emitted — importers' tests
// read those (`Plans`, `ReviewBrainBadge`) — and adds the `status--<tone>` hue
// `ui/_badge.scss` declares after them, so the tone paint wins by source order.
// `neutral` is `.status` itself and adds no hue modifier (an inert class is how
// a typo'd one hides).
const TONE_CLASS: Readonly<Record<StatusTone, string>> = {
  success: 'badge status status--success',
  live: 'badge status status--live',
  warning: 'badge status badge--secondary status--warning',
  danger: 'badge status badge--destructive status--danger',
  neutral: 'badge status badge--secondary',
};

export function StatusPill(props: { tone?: StatusTone; children: JSX.Element }): JSX.Element {
  const tone = (): StatusTone => props.tone ?? 'neutral';
  return (
    <span class={TONE_CLASS[tone()]}>
      <Show when={tone() === 'live'}>
        <span class="status__dot" aria-hidden="true" />
      </Show>
      {props.children}
    </span>
  );
}
